/**
 * 站点级 `WebSite` JSON-LD 的纯函数层。
 *
 * `buildWebSiteLd()` 输出 Requirement 3.6 定义的 7 键对象：`@context`、`@type`、
 * `name`、`url`、`inLanguage`、`publisher`、`potentialAction`。与 `organization.ts`
 * 不同，这 7 个键全部恒非空——`WebSite` JSON-LD 没有类似 `contactPoint` 那样依赖
 * 源数据存在性的可选键，因此本模块没有 `omittedKeys` 的概念。
 *
 * `name` 与 `publisher.name` 必须与同页 `Organization` 的 `name` 逐字符一致
 * （需求 3.6、Requirement 4.2/4.6 对 `provider`/`publisher` 一致性的同类要求），
 * 因此复用 `organization.ts` 的 `resolveOrganizationName()` 而不是各自解析一遍。
 *
 * 全部为纯函数：不读 DOM、不发网络请求、不读全局状态，Node 与浏览器结果一致。
 *
 * 对应需求：3.1、3.6、3.12、3.13
 */

import { SITE_ORIGIN } from "../constants";
import type { OrganizationLd, OrganizationProfile } from "./organization";
import { buildOrganizationLd, resolveOrganizationName } from "./organization";

/** 站点首页绝对地址，`url` 固定取值（需求 3.6） */
const WEBSITE_URL = `${SITE_ORIGIN}/`;

/** `inLanguage` 固定取值（需求 3.6） */
const IN_LANGUAGE = "zh-CN";

/** `potentialAction.target` 的查询词占位符，schema.org `SearchAction` 约定写法（需求 3.6） */
const SEARCH_TERM_PLACEHOLDER = "{search_term_string}";

/** `potentialAction.target` 绝对地址，含 1 个查询词占位符（需求 3.6） */
const SEARCH_ACTION_TARGET = `${SITE_ORIGIN}/search?q=${SEARCH_TERM_PLACEHOLDER}`;

/** `potentialAction.query-input` 固定取值（需求 3.6 仅要求非空字符串） */
const QUERY_INPUT = `required name=search_term_string`;

/** `WebSite` JSON-LD 的 `potentialAction` 键（需求 3.6） */
export type WebSiteSearchAction = {
  "@type": "SearchAction";
  target: string;
  "query-input": string;
};

/** `WebSite` JSON-LD 的 `publisher` 键：引用同页 `Organization`（需求 3.6） */
export type WebSitePublisher = Pick<OrganizationLd, "@type" | "name">;

/** `buildWebSiteLd()` 输出的 7 键对象 */
export type WebSiteLd = {
  "@context": "https://schema.org";
  "@type": "WebSite";
  name: string;
  url: string;
  inLanguage: typeof IN_LANGUAGE;
  publisher: WebSitePublisher;
  potentialAction: WebSiteSearchAction;
};

/**
 * 组装 `WebSite` JSON-LD（需求 3.1、3.6、3.12、3.13）。
 *
 * - `name`：与同页 `Organization` 的 `name` 逐字符一致，复用
 *   `resolveOrganizationName()`（`companyName` → `siteName` → 兜底常量，归一到 2–60 字符）
 * - `url`：固定为站点首页绝对地址，全站每个页面逐键一致（需求 3.13）
 * - `inLanguage`：固定 `zh-CN`
 * - `publisher`：`{ "@type": "Organization", name }`，`name` 与本对象的 `name` 相同
 * - `potentialAction`：`@type` 为 `SearchAction`，`target` 为含 1 个查询词占位符
 *   （`{search_term_string}`）的绝对地址，`query-input` 为非空字符串
 *
 * `profile` 缺失或字段全部为空时仍能正常输出：`name` 走兜底常量，
 * 其余 6 键均为固定取值或由 `name` 派生，因此 `WebSite` JSON-LD 不存在
 * 「无 profile 时省略部分键」的兜底路径——需求 3.12 的六键兜底只针对 `Organization`。
 *
 * @param profile `GET /api/front/geo/site-profile` 的响应；可缺失
 */
export function buildWebSiteLd(profile?: OrganizationProfile | null): WebSiteLd {
  const name = resolveOrganizationName(profile);

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url: WEBSITE_URL,
    inLanguage: IN_LANGUAGE,
    publisher: {
      "@type": "Organization",
      name,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: SEARCH_ACTION_TARGET,
      "query-input": QUERY_INPUT,
    },
  };
}

/**
 * 一次性组装同页共用的 `Organization` + `WebSite` 两段 Site_JsonLd（需求 3.1）。
 *
 * 两段共用同一个 `profile` 输入，`name` 天然一致（需求 3.6 对 `publisher.name`
 * 与 `Organization.name` 一致性的要求由此在类型层面即成立，无需额外校验）。
 * 供运行时与构建期一次调用取两段，避免各自解析一遍 `profile` 而产生分歧。
 *
 * @param profile `GET /api/front/geo/site-profile` 的响应；可缺失，走兜底路径
 */
export function buildSiteJsonLd(profile?: OrganizationProfile | null): {
  organization: ReturnType<typeof buildOrganizationLd>;
  website: WebSiteLd;
} {
  return {
    organization: buildOrganizationLd(profile),
    website: buildWebSiteLd(profile),
  };
}
